import { EPreferences } from "@/enums/prefernces";
import { emailRegex } from "@/utils/regex";
import { EAddressType } from "@/widgets/quote-new/business-widget/address-widget/state";

/** Matches `PreferenceValue.Key` in admin DB for CUSTOMERS_REQUIRED_FIELDS */
export const CustomerMainRequiredKey = {
  EIN: "EIN",
  CPA_CODE: "CPA_CODE",
  PHONE: "PHONE",
  MAIN_CONTACT_NAME: "MAIN_CONTACT_NAME",
  MOBILE: "MOBILE",
  EMAIL: "EMAIL",
  SALES_REPRESENTATIVE: "SALES_REPRESENTATIVE",
  PAYMENT_TERMS: "PAYMENT_TERMS",
  ADDRESS: "ADDRESS",
  CONTACT: "CONTACT",
} as const;

/** Matches `PreferenceValue.Key` for CUSTOMERS_ADDRESSES_REQUIRED_FIELDS */
export const CustomerAddressSubFieldKey = {
  COUNTRY: "COUNTRY",
  CITY: "CITY",
  STREET: "STREET",
  HOME: "HOME",
  ENTRANCE: "ENTRANCE",
  FLOOR: "FLOOR",
  APARTMENT: "APARTMENT",
  ZIP_CODE: "ZIP_CODE",
  PO_BOX: "PO_BOX",
  /** US only: maps to `country` dropdown (admin label may say County). */
  COUNTY: "COUNTY",
  REMARKS: "REMARKS",
  STATE: "STATE",
} as const;

/** Fields on the US country/state address form (quote + customer card). */
const US_COUNTRY_STATE_ADDRESS_FORM_KEYS = new Set<string>([
  CustomerAddressSubFieldKey.COUNTRY,
  CustomerAddressSubFieldKey.COUNTY,
  CustomerAddressSubFieldKey.STREET,
  CustomerAddressSubFieldKey.HOME,
  CustomerAddressSubFieldKey.CITY,
  CustomerAddressSubFieldKey.STATE,
  CustomerAddressSubFieldKey.ZIP_CODE,
]);

/** Fields on the standard (non-US) address form. */
const INTL_ADDRESS_FORM_KEYS = new Set<string>([
  CustomerAddressSubFieldKey.CITY,
  CustomerAddressSubFieldKey.STREET,
  CustomerAddressSubFieldKey.HOME,
  CustomerAddressSubFieldKey.ENTRANCE,
  CustomerAddressSubFieldKey.FLOOR,
  CustomerAddressSubFieldKey.APARTMENT,
  CustomerAddressSubFieldKey.ZIP_CODE,
  CustomerAddressSubFieldKey.PO_BOX,
  CustomerAddressSubFieldKey.REMARKS,
]);

/** Matches `PreferenceValue.Key` for CUSTOMERS_CONTACTS_REQUIRED_FIELDS */
export const CustomerContactSubFieldKey = {
  FIRST_NAME: "FIRST_NAME",
  LAST_NAME: "LAST_NAME",
  MOBILE: "MOBILE",
  EMAIL: "EMAIL",
} as const;

export type CustomerRequiredFieldRules = {
  main: Set<string>;
  addressSub: Set<string>;
  contactSub: Set<string>;
};

/** Which preference trio (customer vs supplier card) applies */
export type PartyRequiredFieldPreferenceScope = "customer" | "supplier";

function normalizeAddressPreferenceKey(raw: string): string {
  return String(raw ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
}

/** Maps admin keys like CUSTOMER_REQ_ADDRESS → ADDRESS, CUSTOMER_CONTACT_FIRST_NAME → FIRST_NAME. */
function normalizeMainRequiredPreferenceKey(raw: string): string {
  const normalized = normalizeAddressPreferenceKey(raw);
  const reqMatch = normalized.match(/^(?:CUSTOMER|SUPPLIER)_REQ_(.+)$/);
  if (reqMatch?.[1]) {
    return reqMatch[1];
  }
  const contactMatch = normalized.match(/^(?:CUSTOMER|SUPPLIER)_CONTACT_(.+)$/);
  if (contactMatch?.[1]) {
    return contactMatch[1];
  }
  return normalized;
}

export function isPrintHousePreferenceEnabled(value: unknown): boolean {
  if (value === true) {
    return true;
  }
  if (value === false || value == null) {
    return false;
  }
  const normalized = String(value).trim().toLowerCase();
  if (normalized === "false" || normalized === "no" || normalized === "0") {
    return false;
  }
  if (normalized === "true" || normalized === "yes" || normalized === "1") {
    return true;
  }
  // Yes/No dropdowns may store the selected option id (UUID) instead of "true".
  return normalized.length > 0;
}

function mainRulesHas(
  rules: CustomerRequiredFieldRules,
  fieldKey: string,
): boolean {
  const normalizedTarget = normalizeMainRequiredPreferenceKey(fieldKey);
  return Array.from(rules.main).some(
    (key) => normalizeMainRequiredPreferenceKey(key) === normalizedTarget,
  );
}

/** Resolve PreferenceValue row to address/main field key (CITY, COUNTRY, …). */
function resolvePreferenceOptionKey(opt: any): string | undefined {
  if (!opt) {
    return undefined;
  }
  const value = opt.value ?? opt.Value;
  if (value != null && String(value).trim().length > 0) {
    const valueStr = String(value).trim();
    const addrMatch = valueStr.match(/^(?:CUSTOMER|SUPPLIER)_ADDR_(.+)$/i);
    if (addrMatch?.[1]) {
      return normalizeAddressPreferenceKey(addrMatch[1]);
    }
    return normalizeMainRequiredPreferenceKey(valueStr);
  }

  const direct = opt.key ?? opt.Key ?? opt.valueKey ?? opt.ValueKey;
  if (direct != null && String(direct).trim().length > 0) {
    return normalizeMainRequiredPreferenceKey(String(direct));
  }

  const label = opt.label ?? opt.name ?? opt.text;
  if (label != null && String(label).trim().length > 0) {
    const normalizedLabel = normalizeAddressPreferenceKey(String(label));
    if (
      normalizedLabel === CustomerAddressSubFieldKey.COUNTRY ||
      normalizedLabel === "CUSTOMER_ADDR_COUNTRY"
    ) {
      return CustomerAddressSubFieldKey.COUNTRY;
    }
    if (
      normalizedLabel === CustomerAddressSubFieldKey.COUNTY ||
      normalizedLabel === "CUSTOMER_ADDR_COUNTY"
    ) {
      return CustomerAddressSubFieldKey.COUNTY;
    }
  }

  return undefined;
}

function isCountryPreferenceKey(fieldKey: string): boolean {
  return (
    normalizeAddressPreferenceKey(fieldKey) === CustomerAddressSubFieldKey.COUNTRY
  );
}

function isCountyPreferenceKey(fieldKey: string): boolean {
  return (
    normalizeAddressPreferenceKey(fieldKey) === CustomerAddressSubFieldKey.COUNTY
  );
}

/**
 * US country/state address form — only when the print house is US and the admin
 * preference is enabled. Non-US print houses use the international form with no
 * Country or County fields.
 */
export function resolveUsesUsCountryStateAddressForm(
  isUsPrintHouse: boolean,
  hasCountryAndStateAddressPreference: boolean,
): boolean {
  return isUsPrintHouse && hasCountryAndStateAddressPreference;
}

/** US country dropdown — required when COUNTRY or COUNTY is in address preferences. */
export function isUsAddressRegionRequired(
  rules: CustomerRequiredFieldRules,
): boolean {
  return (
    addressSubRulesHas(rules, CustomerAddressSubFieldKey.COUNTRY) ||
    addressSubRulesHas(rules, CustomerAddressSubFieldKey.COUNTY)
  );
}

/** Label for the US country dropdown (admin "County" maps to this field). */
export function getUsCountryDropdownLabelKey(
  rules: CustomerRequiredFieldRules,
): string {
  if (
    addressSubRulesHas(rules, CustomerAddressSubFieldKey.COUNTY) &&
    !addressSubRulesHas(rules, CustomerAddressSubFieldKey.COUNTRY)
  ) {
    return "customers.modal.county";
  }
  return "customers.modal.country";
}

function shouldValidateAddressSubFieldForForm(
  fieldKey: string,
  usesCountryStateAddressForm: boolean,
): boolean {
  const normalized = normalizeAddressPreferenceKey(fieldKey);
  if (usesCountryStateAddressForm) {
    return US_COUNTRY_STATE_ADDRESS_FORM_KEYS.has(normalized);
  }
  if (
    isCountryPreferenceKey(normalized) ||
    isCountyPreferenceKey(normalized) ||
    normalized === CustomerAddressSubFieldKey.STATE
  ) {
    return false;
  }
  return INTL_ADDRESS_FORM_KEYS.has(normalized);
}

function addressSubRulesHas(
  rules: CustomerRequiredFieldRules,
  fieldKey: string,
): boolean {
  const normalized = normalizeAddressPreferenceKey(fieldKey);
  return Array.from(rules.addressSub).some(
    (key) => normalizeAddressPreferenceKey(key) === normalized,
  );
}

export function parseMultiSelectKeysFromPreference(preference: any): string[] {
  if (
    preference == null ||
    preference?.value === undefined ||
    preference?.value === null
  ) {
    return [];
  }
  const ids = String(preference.value)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const values = preference.preferenceValues ?? preference.PreferenceValues;
  if (ids.length === 0) {
    return [];
  }
  if (!Array.isArray(values) || values.length === 0) {
    return ids
      .map((id) => normalizeMainRequiredPreferenceKey(id))
      .filter((k): k is string => !!k && String(k).length > 0);
  }
  return ids
    .map((id) => {
      const opt = values.find(
        (v: any) =>
          String(v.id) === String(id) ||
          String(v.key ?? v.Key ?? "") === String(id) ||
          String(v.valueKey ?? v.ValueKey ?? "") === String(id),
      );
      return resolvePreferenceOptionKey(opt ?? { key: id });
    })
    .filter((k): k is string => !!k && String(k).length > 0);
}

export function buildCustomerRequiredFieldRules(
  preferences: any[],
  scope: PartyRequiredFieldPreferenceScope = "customer",
): CustomerRequiredFieldRules {
  const codes =
    scope === "supplier"
      ? {
          main: EPreferences.SUPPLIERS_REQUIRED_FIELDS,
          addressSub: EPreferences.SUPPLIERS_ADDRESSES_REQUIRED_FIELDS,
          contactSub: EPreferences.SUPPLIERS_CONTACTS_REQUIRED_FIELDS,
        }
      : {
          main: EPreferences.CUSTOMERS_REQUIRED_FIELDS,
          addressSub: EPreferences.CUSTOMERS_ADDRESSES_REQUIRED_FIELDS,
          contactSub: EPreferences.CUSTOMERS_CONTACTS_REQUIRED_FIELDS,
        };

  const findPref = (code: string) =>
    preferences?.find((p: any) => p?.code === code);
  const main = new Set(
    parseMultiSelectKeysFromPreference(findPref(codes.main)),
  );
  const addressSub = new Set(
    parseMultiSelectKeysFromPreference(findPref(codes.addressSub)),
  );
  const contactSub = new Set(
    parseMultiSelectKeysFromPreference(findPref(codes.contactSub)),
  );
  return { main, addressSub, contactSub };
}

/** Preference code for quote document delivery vs billing address modals. */
export function getQuoteDocumentAddressPreferenceCode(
  addressType: EAddressType,
  scope: PartyRequiredFieldPreferenceScope = "customer",
): string {
  if (scope === "supplier") {
    return EPreferences.SUPPLIERS_ADDRESSES_REQUIRED_FIELDS;
  }
  return addressType === EAddressType.BILLING
    ? EPreferences.CUSTOMERS_BILLING_ADDRESSES_REQUIRED_FIELDS
    : EPreferences.CUSTOMERS_DELIVERY_ADDRESSES_REQUIRED_FIELDS;
}

/** City was always required on quote addresses before delivery/billing-specific prefs existed. */
const DEFAULT_QUOTE_DOCUMENT_ADDRESS_SUB_FIELDS = [
  CustomerAddressSubFieldKey.CITY,
];

/**
 * Address sub-field rules for quote delivery/billing modals.
 * Uses only Quotes-tab prefs (billing/delivery). Does not merge
 * Customers/Suppliers "Required address lines" from the customer card.
 */
export function buildQuoteDocumentAddressRequiredFieldRules(
  preferences: any[],
  addressType: EAddressType,
  scope: PartyRequiredFieldPreferenceScope = "customer",
): CustomerRequiredFieldRules {
  const code = getQuoteDocumentAddressPreferenceCode(addressType, scope);
  const pref = preferences?.find((p: any) => p?.code === code);
  let addressSubKeys = parseMultiSelectKeysFromPreference(pref);

  if (addressSubKeys.length === 0 && pref == null) {
    addressSubKeys = [...DEFAULT_QUOTE_DOCUMENT_ADDRESS_SUB_FIELDS];
  }

  const addressSub = new Set(addressSubKeys);
  return {
    main: new Set<string>(),
    addressSub,
    contactSub: new Set<string>(),
  };
}

function validateQuoteDocumentAddressSubFields(
  quoteItemValue: any,
  preferences: any[],
  addressFormIsUs: boolean,
  scope: PartyRequiredFieldPreferenceScope = "customer",
): string[] {
  const missing: string[] = [];
  const clientAddresses =
    quoteItemValue?.client?.addresses ??
    quoteItemValue?.clientAddresses ??
    [];

  const resolveForValidation = (documentAddress: any) => {
    const resolved = resolveQuoteDocumentAddressForForm(
      documentAddress,
      clientAddresses,
    );
    return resolved ?? normalizeQuoteDocumentAddressShape(documentAddress);
  };

  const deliveryRows = (quoteItemValue?.documentDeliveryAddresses ?? [])
    .filter((a: any) => a && !isPlaceholderQuoteDocumentAddress(a))
    .map(resolveForValidation);
  const billingRows = (quoteItemValue?.documentBillingAddresses ?? [])
    .filter((a: any) => a && !isPlaceholderQuoteDocumentAddress(a))
    .map(resolveForValidation);

  const deliveryRules = buildQuoteDocumentAddressRequiredFieldRules(
    preferences,
    EAddressType.DELIVERY,
    scope,
  );
  const billingRules = buildQuoteDocumentAddressRequiredFieldRules(
    preferences,
    EAddressType.BILLING,
    scope,
  );

  deliveryRows.forEach((address) => {
    missing.push(
      ...getMissingAddressSubFieldsByPreference(
        address,
        deliveryRules,
        addressFormIsUs,
      ),
    );
  });
  billingRows.forEach((address) => {
    missing.push(
      ...getMissingAddressSubFieldsByPreference(
        address,
        billingRules,
        addressFormIsUs,
      ),
    );
  });

  return Array.from(new Set(missing));
}

function isNonEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "number") return !Number.isNaN(value);
  return true;
}

function getAddressFieldValue(
  address: any,
  fieldKey: string,
  isUs: boolean,
): unknown {
  switch (normalizeAddressPreferenceKey(fieldKey)) {
    case CustomerAddressSubFieldKey.COUNTRY:
      return isUs ? (address?.country ?? address?.Country) : undefined;
    case CustomerAddressSubFieldKey.CITY:
      return address?.city;
    case CustomerAddressSubFieldKey.STREET:
      return isUs ? address?.address2 : address?.street;
    case CustomerAddressSubFieldKey.HOME:
      return isUs ? address?.address3 : getAddressHomeNumber(address);
    case CustomerAddressSubFieldKey.ENTRANCE:
      return isUs ? undefined : address?.entry;
    case CustomerAddressSubFieldKey.FLOOR:
      return isUs ? undefined : address?.floor;
    case CustomerAddressSubFieldKey.APARTMENT:
      return isUs ? undefined : address?.apartment;
    case CustomerAddressSubFieldKey.ZIP_CODE:
      return address?.zipCode;
    case CustomerAddressSubFieldKey.STATE:
      return address?.state ?? address?.State;
    case CustomerAddressSubFieldKey.PO_BOX:
      return isUs ? undefined : address?.postbox;
    case CustomerAddressSubFieldKey.COUNTY:
      return isUs ? (address?.country ?? address?.Country) : undefined;
    case CustomerAddressSubFieldKey.REMARKS:
      return isUs ? undefined : address?.notes;
    default:
      return undefined;
  }
}

function missingKeyLabelForAddressField(fieldKey: string): string {
  switch (normalizeAddressPreferenceKey(fieldKey)) {
    case CustomerAddressSubFieldKey.COUNTRY:
      return "customers.modal.country";
    case CustomerAddressSubFieldKey.CITY:
      return "customers.modal.city";
    case CustomerAddressSubFieldKey.STREET:
      return "customers.modal.street";
    case CustomerAddressSubFieldKey.HOME:
      return "customers.modal.home";
    case CustomerAddressSubFieldKey.ENTRANCE:
      return "customers.modal.entrance";
    case CustomerAddressSubFieldKey.FLOOR:
      return "customers.modal.floor";
    case CustomerAddressSubFieldKey.APARTMENT:
      return "customers.modal.apartment";
    case CustomerAddressSubFieldKey.ZIP_CODE:
      return "customers.modal.zipCode";
    case CustomerAddressSubFieldKey.STATE:
      return "customers.modal.state";
    case CustomerAddressSubFieldKey.PO_BOX:
      return "customers.modal.po";
    case CustomerAddressSubFieldKey.COUNTY:
      return "customers.modal.county";
    case CustomerAddressSubFieldKey.REMARKS:
      return "customers.modal.remarks";
    default:
      return "modal.RequiredFields";
  }
}

function missingKeyLabelForContactField(fieldKey: string): string {
  switch (fieldKey) {
    case CustomerContactSubFieldKey.FIRST_NAME:
      return "customers.modal.firstName";
    case CustomerContactSubFieldKey.LAST_NAME:
      return "customers.modal.lastName";
    case CustomerContactSubFieldKey.MOBILE:
      return "customers.modal.mobile";
    case CustomerContactSubFieldKey.EMAIL:
      return "customers.modal.email";
    default:
      return "modal.RequiredFields";
  }
}

export function effectiveAddressSubFieldRequired(
  rules: CustomerRequiredFieldRules,
  _addressMainSectionEnabled: boolean,
  fieldKey: string,
): boolean {
  void _addressMainSectionEnabled;
  // Line prefs apply independently of main Address (at least one).
  return addressSubRulesHas(rules, fieldKey);
}

/** Sub-field rules from an address preference set (no main ADDRESS gate). */
export function isAddressSubFieldRequiredByPreference(
  rules: CustomerRequiredFieldRules,
  fieldKey: string,
  usesCountryStateAddressForm?: boolean,
): boolean {
  if (usesCountryStateAddressForm === false) {
    if (isCountryPreferenceKey(fieldKey)) {
      return false;
    }
    if (isCountyPreferenceKey(fieldKey)) {
      return false;
    }
    if (normalizeAddressPreferenceKey(fieldKey) === CustomerAddressSubFieldKey.STATE) {
      return false;
    }
  }
  return addressSubRulesHas(rules, fieldKey);
}

export function getMissingAddressSubFieldsByPreference(
  address: any,
  rules: CustomerRequiredFieldRules,
  usesCountryStateAddressForm: boolean,
): string[] {
  const missing: string[] = [];

  if (usesCountryStateAddressForm && isUsAddressRegionRequired(rules)) {
    const regionValue = getAddressFieldValue(
      address,
      CustomerAddressSubFieldKey.COUNTY,
      true,
    );
    if (!isNonEmpty(regionValue)) {
      if (addressSubRulesHas(rules, CustomerAddressSubFieldKey.COUNTY)) {
        missing.push(
          missingKeyLabelForAddressField(CustomerAddressSubFieldKey.COUNTY),
        );
      } else {
        missing.push(
          missingKeyLabelForAddressField(CustomerAddressSubFieldKey.COUNTRY),
        );
      }
    }
  }

  rules.addressSub.forEach((fieldKey) => {
    if (
      usesCountryStateAddressForm &&
      (isCountryPreferenceKey(fieldKey) || isCountyPreferenceKey(fieldKey))
    ) {
      return;
    }
    if (!shouldValidateAddressSubFieldForForm(fieldKey, usesCountryStateAddressForm)) {
      return;
    }
    const val = getAddressFieldValue(
      address,
      fieldKey,
      usesCountryStateAddressForm,
    );
    if (!isNonEmpty(val)) {
      missing.push(missingKeyLabelForAddressField(fieldKey));
    }
  });
  return Array.from(new Set(missing));
}

export function effectiveContactSubFieldRequired(
  rules: CustomerRequiredFieldRules,
  _contactMainSectionEnabled: boolean,
  fieldKey: string,
): boolean {
  void _contactMainSectionEnabled;
  // Line prefs apply independently of main Contact (at least one).
  return rules.contactSub.has(fieldKey);
}

export function getMissingContactFirstNames(
  filteredContacts: any[] | undefined,
): string[] {
  const missing: string[] = [];
  filteredContacts?.forEach((contact) => {
    if (!isNonEmpty(contact?.firstName)) {
      missing.push("customers.modal.firstName");
    }
  });
  return missing;
}

export type CustomerMissingFieldsBySection = {
  general: string[];
  address: string[];
  contact: string[];
};

export type IsValidCustomerResult = {
  isValid: boolean;
  missingFields: string[];
  missingBySection: CustomerMissingFieldsBySection;
};

/** Builds the customer-card required-fields alert with Address/Contact tab labels. */
export function formatCustomerMissingRequiredFieldsAlert(
  missingBySection: CustomerMissingFieldsBySection,
  translate: (key: string) => string,
): string {
  const parts: string[] = [];

  if (missingBySection.general.length > 0) {
    parts.push(missingBySection.general.map((key) => translate(key)).join(", "));
  }
  if (missingBySection.address.length > 0) {
    parts.push(
      `${translate("customers.validation.requiredAddressTab")}: ${missingBySection.address
        .map((key) => translate(key))
        .join(", ")}`,
    );
  }
  if (missingBySection.contact.length > 0) {
    parts.push(
      `${translate("customers.validation.requiredContactTab")}: ${missingBySection.contact
        .map((key) => translate(key))
        .join(", ")}`,
    );
  }

  return `${translate("modal.RequiredFields")}: ${parts.join("; ")}`;
}

export const isValidCustomer = (
  customer: any,
  filteredContacts: any[] | undefined,
  filteredAddresses: any[] | undefined,
  _filteredUsers: any[] | undefined,
  rulesInput: CustomerRequiredFieldRules | null | undefined,
  options: { isSupplier: boolean; showCpaCode: boolean },
  addressFormIsUs: boolean,
): IsValidCustomerResult => {
  const general: string[] = [];
  const address: string[] = [];
  const contact: string[] = [];
  const rules = rulesInput ?? {
    main: new Set<string>(),
    addressSub: new Set<string>(),
    contactSub: new Set<string>(),
  };
  const { isSupplier, showCpaCode } = options;
  void _filteredUsers;

  if (!isNonEmpty(customer?.name)) {
    general.push(
      isSupplier ? "suppliers.supplierName" : "customers.modal.customerName",
    );
  }
  if (!customer?.clientTypeId) {
    if (isSupplier) {
      general.push("suppliers.supplierType");
    } else {
      general.push("customers.modal.clientType");
    }
  }

  const main = rules.main;

  if (main.has(CustomerMainRequiredKey.EIN) && !customer?.buisnessNumber) {
    general.push("customers.modal.vatNO");
  }

  if (
    main.has(CustomerMainRequiredKey.CPA_CODE) &&
    showCpaCode &&
    !customer?.cpaClientCode
  ) {
    general.push("customers.modal.CPAcode");
  }

  if (main.has(CustomerMainRequiredKey.PHONE) && !customer?.tel1) {
    general.push("customers.modal.phone");
  }

  if (
    main.has(CustomerMainRequiredKey.MAIN_CONTACT_NAME) &&
    !isNonEmpty(customer?.mainContactName)
  ) {
    general.push("customers.modal.mainContactName");
  }

  if (main.has(CustomerMainRequiredKey.MOBILE) && !customer?.phone) {
    general.push("customers.modal.mobile");
  }

  if (main.has(CustomerMainRequiredKey.EMAIL)) {
    const m = customer?.mail;
    if (!isNonEmpty(m) || !emailRegex.test(String(m))) {
      general.push("customers.modal.email");
    }
  }

  if (
    main.has(CustomerMainRequiredKey.SALES_REPRESENTATIVE) &&
    !customer?.agentId
  ) {
    general.push("customers.modal.agent");
  }

  const term = customer?.termOfPayment;
  if (
    main.has(CustomerMainRequiredKey.PAYMENT_TERMS) &&
    (term === undefined || term === null || term === "")
  ) {
    general.push("accounting.paymentTerms");
  }

  const atLeastOneAddressKey = isSupplier
    ? "suppliers.validation.atLeastOneAddress"
    : "customers.validation.atLeastOneAddress";
  const atLeastOneContactKey = isSupplier
    ? "suppliers.validation.atLeastOneContact"
    : "customers.validation.atLeastOneContact";

  // Main: Address (at least one) — independent of line prefs
  if (main.has(CustomerMainRequiredKey.ADDRESS) && !filteredAddresses?.length) {
    address.push(atLeastOneAddressKey);
  }

  // Required address lines — independent of main ADDRESS
  if (rules.addressSub.size > 0) {
    if (!filteredAddresses?.length) {
      rules.addressSub.forEach((fieldKey) => {
        if (!shouldValidateAddressSubFieldForForm(fieldKey, addressFormIsUs)) {
          return;
        }
        address.push(missingKeyLabelForAddressField(fieldKey));
      });
    } else {
      filteredAddresses.forEach((addr) => {
        address.push(
          ...getMissingAddressSubFieldsByPreference(
            addr,
            rules,
            addressFormIsUs,
          ),
        );
      });
    }
  }

  // Main: Contact (at least one) — independent of line prefs
  if (main.has(CustomerMainRequiredKey.CONTACT) && !filteredContacts?.length) {
    contact.push(atLeastOneContactKey);
  }

  // Required contact lines — independent of main CONTACT
  if (rules.contactSub.size > 0) {
    if (!filteredContacts?.length) {
      rules.contactSub.forEach((fieldKey) => {
        contact.push(missingKeyLabelForContactField(fieldKey));
      });
    } else {
      filteredContacts.forEach((contactRow) => {
        rules.contactSub.forEach((fieldKey) => {
          let val: unknown;
          if (fieldKey === CustomerContactSubFieldKey.FIRST_NAME)
            val = contactRow?.firstName;
          else if (fieldKey === CustomerContactSubFieldKey.LAST_NAME)
            val = contactRow?.lastName;
          else if (fieldKey === CustomerContactSubFieldKey.MOBILE)
            val = contactRow?.phone ?? contactRow?.mobile;
          else if (fieldKey === CustomerContactSubFieldKey.EMAIL)
            val = contactRow?.mail ?? contactRow?.email;

          if (fieldKey === CustomerContactSubFieldKey.EMAIL) {
            if (!isNonEmpty(val) || !emailRegex.test(String(val))) {
              contact.push(missingKeyLabelForContactField(fieldKey));
            }
          } else if (!isNonEmpty(val)) {
            contact.push(missingKeyLabelForContactField(fieldKey));
          }
        });
      });
    }
  }

  const missingBySection: CustomerMissingFieldsBySection = {
    general: Array.from(new Set(general)),
    address: Array.from(new Set(address)),
    contact: Array.from(new Set(contact)),
  };
  const missingFields = Array.from(
    new Set([
      ...missingBySection.general,
      ...missingBySection.address,
      ...missingBySection.contact,
    ]),
  );

  return {
    isValid: missingFields.length === 0,
    missingFields,
    missingBySection,
  };
};

function isPlaceholderQuoteDocumentContact(c: any): boolean {
  const name = String(c?.contactName ?? c?.name ?? "").trim();
  const mail = String(c?.contactMail ?? c?.email ?? c?.mail ?? "").trim();
  const phone = String(c?.contactPhone ?? c?.phone ?? "").trim();
  return !name && !mail && !phone;
}

/** Map quote document contacts into the shape consumed by `isValidCustomer` CONTACT rules. */
export function buildQuoteContactRowsForValidation(
  documentContacts: any[] | undefined | null,
): any[] {
  if (!Array.isArray(documentContacts)) return [];
  return documentContacts
    .filter((c) => c && !isPlaceholderQuoteDocumentContact(c))
    .map((c) => {
      const rawName = String(c?.contactName ?? c?.name ?? "").trim();
      const parts = rawName.split(/\s+/).filter(Boolean);
      return {
        firstName: parts[0] ?? "",
        lastName: parts.slice(1).join(" ") ?? "",
        mail: c?.contactMail ?? c?.email ?? c?.mail ?? "",
        phone: c?.contactPhone ?? c?.phone ?? "",
      };
    });
}

function isPlaceholderQuoteDocumentAddress(a: any): boolean {
  const pick = (v: unknown) =>
    v !== null &&
    v !== undefined &&
    typeof v === "string" &&
    v.trim().length > 0;

  return !(
    pick(a?.city ?? a?.City) ||
    pick(a?.street ?? a?.Street) ||
    pick(a?.address2 ?? a?.Address2) ||
    pick(a?.address3 ?? a?.Address3) ||
    pick(a?.zipCode ?? a?.zIPCode ?? a?.ZIPCode) ||
    pick(a?.country ?? a?.Country) ||
    pick(a?.state ?? a?.State) ||
    pick(a?.notes ?? a?.Notes)
  );
}

/** Home / house number from API (camelCase, PascalCase, or legacy `home`). */
export function getAddressHomeNumber(raw: any): string {
  if (!raw) {
    return "";
  }
  const value =
    raw.homeNumber ??
    raw.HomeNumber ??
    raw.home ??
    raw.Home ??
    raw.address3 ??
    raw.Address3;
  if (value === null || value === undefined) {
    return "";
  }
  return String(value).trim();
}

/** Normalize a quote document address row for validation and display. */
export function normalizeQuoteDocumentAddressShape(raw: any): any {
  const address2 = raw?.address2 ?? raw?.Address2 ?? "";
  const address3Raw = raw?.address3 ?? raw?.Address3 ?? "";
  const homeNumber = getAddressHomeNumber(raw) || address3Raw;
  const address3 = address3Raw || homeNumber;
  return {
    city: raw?.city ?? raw?.City,
    street: raw?.street ?? raw?.Street,
    address2,
    address3,
    homeNumber,
    entry: raw?.entry ?? raw?.Entry,
    floor: raw?.floor ?? raw?.Floor,
    apartment: raw?.apartment ?? raw?.Apartment,
    zipCode: raw?.zipCode ?? raw?.zIPCode ?? raw?.ZIPCode,
    postbox: raw?.postbox ?? raw?.Postbox,
    county: raw?.county ?? raw?.County,
    country: raw?.country ?? raw?.Country,
    state: raw?.state ?? raw?.State,
    notes: raw?.notes ?? raw?.Notes,
  };
}

function findLinkedClientAddress(
  documentAddress: any,
  clientAddresses: any[],
): any | undefined {
  if (!documentAddress || !clientAddresses?.length) {
    return undefined;
  }
  const linkedId =
    documentAddress.addressID ??
    documentAddress.AddressID ??
    documentAddress.clientAddressId;
  if (linkedId) {
    const byLink = clientAddresses.find(
      (a) => a.id === linkedId || String(a.id) === String(linkedId),
    );
    if (byLink) {
      return byLink;
    }
  }
  const byDocId = clientAddresses.find((a) => a.id === documentAddress.id);
  if (byDocId) {
    return byDocId;
  }
  const docCity = documentAddress.city ?? documentAddress.City;
  const docStreet = documentAddress.street ?? documentAddress.Street;
  if (!docCity && !docStreet) {
    return undefined;
  }
  return clientAddresses.find((a) => {
    const sameCity = !docCity || a.city === docCity;
    const sameStreet = !docStreet || a.street === docStreet;
    const sameType =
      documentAddress.type == null || a.type === documentAddress.type;
    return sameCity && sameStreet && sameType;
  });
}

/** Merge document row with client address so homeNumber is available in quote modals. */
export function resolveQuoteDocumentAddressForForm(
  documentAddress: any,
  clientAddresses: any[] = [],
): any {
  if (!documentAddress) {
    return null;
  }
  const clientMatch = findLinkedClientAddress(documentAddress, clientAddresses);
  const merged = clientMatch
    ? { ...clientMatch, ...documentAddress }
    : { ...documentAddress };
  const normalized = normalizeQuoteDocumentAddressShape(merged);
  const homeNumber =
    getAddressHomeNumber(documentAddress) ||
    getAddressHomeNumber(clientMatch) ||
    normalized.homeNumber;
  return {
    ...merged,
    ...normalized,
    homeNumber,
    id: documentAddress.id ?? merged.id,
    addressID:
      documentAddress.addressID ??
      documentAddress.AddressID ??
      clientMatch?.id,
  };
}

/** Labeled parts for quote document delivery/billing address summary. */
export function formatQuoteDocumentAddressDisplayParts(
  address: any,
  t: (key: string) => string,
  clientAddresses: any[] = [],
): string[] {
  if (!address) {
    return [];
  }
  const resolved =
    resolveQuoteDocumentAddressForForm(address, clientAddresses) ?? address;
  const normalized = normalizeQuoteDocumentAddressShape(resolved);
  const street =
    resolved?.street ??
    resolved?.Street ??
    resolved?.address2 ??
    resolved?.Address2;
  const home = getAddressHomeNumber(resolved) || normalized.homeNumber;
  return [
    normalized.city && `${t("customers.modal.city")}: ${normalized.city}`,
    street && `${t("customers.modal.street")}: ${street}`,
    home && `${t("customers.modal.home")}: ${home}`,
    normalized.entry &&
      `${t("customers.modal.entrance")}: ${normalized.entry}`,
    normalized.apartment &&
      `${t("customers.modal.apartment")}: ${normalized.apartment}`,
    normalized.floor && `${t("customers.modal.floor")}: ${normalized.floor}`,
    normalized.zipCode &&
      `${t("customers.modal.zipCode")}: ${normalized.zipCode}`,
    normalized.postbox && `${t("customers.modal.po")}: ${normalized.postbox}`,
  ].filter(Boolean) as string[];
}

/**
 * Delivery address is required on Order Now when
 * `CHECK_CUSTOMER_REQUIRED_FIELDS_IN_QUOTE` is on and
 * `CUSTOMERS_REQUIRED_FIELDS` includes Address (CUSTOMER_REQ_ADDRESS).
 */
export function isQuoteDeliveryAddressRequiredByCustomerPreferences(
  printHousePreferences: any[],
  checkCustomerRequiredFieldsInQuote: unknown,
): boolean {
  if (!isPrintHousePreferenceEnabled(checkCustomerRequiredFieldsInQuote)) {
    return false;
  }
  const rules = buildCustomerRequiredFieldRules(
    printHousePreferences ?? [],
    "customer",
  );
  return mainRulesHas(rules, CustomerMainRequiredKey.ADDRESS);
}

/** True when the quote has at least one non-empty delivery address row. */
export function hasFilledQuoteDeliveryAddress(quoteItemValue: any): boolean {
  const delivery = quoteItemValue?.documentDeliveryAddresses ?? [];
  return delivery.some((a) => a && !isPlaceholderQuoteDocumentAddress(a));
}

/** Billing + delivery rows on the quote, normalized for address sub-field validation. */
export function buildQuoteAddressRowsForValidation(quoteItemValue: any): any[] {
  const billing = quoteItemValue?.documentBillingAddresses ?? [];
  const delivery = quoteItemValue?.documentDeliveryAddresses ?? [];
  const all = [...billing, ...delivery];
  return all
    .filter((a) => a && !isPlaceholderQuoteDocumentAddress(a))
    .map(normalizeQuoteDocumentAddressShape);
}

/** Main card fields from quote `client` (+ fallbacks) for CUSTOMERS_REQUIRED_FIELDS (not address/contact). */
export function buildClientProfileFromQuoteForValidation(
  quoteItemValue: any,
): any {
  const c = quoteItemValue?.client ?? {};
  return {
    name:
      c.name ??
      quoteItemValue?.occasionalClientName ??
      quoteItemValue?.customerName,
    clientTypeId: c.clientTypeId,
    buisnessNumber: c.buisnessNumber,
    cpaClientCode: c.cpaClientCode,
    tel1: c.tel1,
    mainContactName: c.mainContactName,
    phone: c.phone,
    mail: c.mail,
    agentId: c.agentId,
    termOfPayment: c.termOfPayment,
  };
}

/** When `CHECK_CUSTOMER_REQUIRED_FIELDS_IN_QUOTE` is on, run before convert-quote-to-order. */
export function validateQuoteCustomerAgainstRequiredFields(
  quoteItemValue: any,
  printHousePreferences: any[],
  deps: { addressFormIsUs: boolean; showCpaCode: boolean },
): { isValid: boolean; missingFields: string[] } {
  const customer = buildClientProfileFromQuoteForValidation(quoteItemValue);
  const contacts = buildQuoteContactRowsForValidation(
    quoteItemValue?.documentContacts,
  );
  const documentAddresses = buildQuoteAddressRowsForValidation(quoteItemValue);
  // On convert-quote-to-order we only enforce the quote-specific preferences
  // (billing/delivery address lines). The main CUSTOMERS_REQUIRED_FIELDS card
  // rules and address/contact sub-rules are intentionally not applied here.
  const quoteOnlyRules: CustomerRequiredFieldRules = {
    main: new Set<string>(),
    addressSub: new Set<string>(),
    contactSub: new Set<string>(),
  };
  const { isValid: coreValid, missingFields: coreMissing } = isValidCustomer(
    customer,
    contacts,
    documentAddresses,
    undefined,
    quoteOnlyRules,
    { isSupplier: false, showCpaCode: deps.showCpaCode },
    deps.addressFormIsUs,
  );
  const documentAddressMissing = validateQuoteDocumentAddressSubFields(
    quoteItemValue,
    printHousePreferences ?? [],
    deps.addressFormIsUs,
    "customer",
  );
  const missingFields = Array.from(
    new Set([...coreMissing, ...documentAddressMissing]),
  );
  return {
    isValid: coreValid && documentAddressMissing.length === 0,
    missingFields,
  };
}
